import { Factory } from "@/data/dao/Factory";
import type { IOfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { OfflineStorage } from "@/data/dao/offline/OfflineStorage";
import { ProfileDao } from "@/data/dao/service/ProfileDao";
import { CovidVerificationMode } from "@/data/model/Enums";
import type { ICovidCertificateEncryptionResult, ICovidCertificateVerifyResult } from "@/data/model/Profile";

import { logError } from "@/shared/logging/errors";

import { TastierClient } from "@/app/TastierClient";
import { Identity } from "@/app/context/Identity";

// singleton
export class UserCovidCertificateService {
    offlineCache: IOfflineStorage;
    profileDao: ProfileDao;
    identityService: Identity;

    covidCertificate?: ICovidCertificateVerifyResult;

    private constructor() {
        this.offlineCache = Factory.getInstance<IOfflineStorage>(OfflineStorage);
        this.profileDao = Factory.getInstance<ProfileDao>(ProfileDao);
        this.identityService = TastierClient.identityContext;
    }

    public getCovidCertificate(): ICovidCertificateVerifyResult | undefined {
        return this.covidCertificate;
    }

    public storeCovidCertificate(cert?: ICovidCertificateEncryptionResult) {
        if (cert === undefined) {
            this.covidCertificate = undefined;
            this.offlineCache.remove(this.localCovidCertCacheKey());
        } else {
            this.covidCertificate = cert;
            this.offlineCache.write(this.localCovidCertCacheKey(), {
                hash: cert.hash,
                secondaryHash: cert.secondaryHash,
                passedModes: cert.passedModes,
            });
        }
    }

    public readCovidCertificate():
        | {
              hash: string;
              secondaryHash?: string;
              passedModes: Array<CovidVerificationMode>;
          }
        | undefined {
        const stored: any = this.offlineCache.read(this.localCovidCertCacheKey());
        if (stored === undefined || stored == null || stored.hash == "") {
            return undefined;
        }

        if (stored.passedModes === undefined && stored !== undefined) {
            return {
                hash: stored,
                secondaryHash: undefined,
                passedModes: [CovidVerificationMode.ThreeG],
            };
        } else {
            let vals = new Array<CovidVerificationMode>();
            if (stored.passedModes instanceof Array) vals = stored.passedModes;
            else if (typeof stored.passedModes === "object") vals = Object.values(stored.passedModes);

            stored.passedModes = vals;
        }

        return stored;
    }

    public async validateCovidCertificate(forMode: CovidVerificationMode): Promise<ICovidCertificateVerifyResult> {
        if (
            this.covidCertificate !== undefined &&
            this.covidCertificate.isValid &&
            this.covidCertificate.passedModes.find((c) => c === forMode) !== undefined
        ) {
            return this.covidCertificate;
        }

        const stored = this.readCovidCertificate();
        if (stored === undefined) {
            this.covidCertificate = undefined;
            throw new Error("No certificate in store.");
        }

        //check if user verified 2 hours ago, if so, dont do again
        const twoHours = 7_200_000;
        const profile = this.identityService.getProfile();
        if (
            stored.passedModes.find((c) => c === forMode) !== undefined &&
            profile !== undefined &&
            profile.lastCovidCertValidation !== undefined &&
            new Date().getTime() - profile.lastCovidCertValidation.getTime() < twoHours
        ) {
            // two hours
            this.covidCertificate = {
                isValid: true,
                passedModes: stored.passedModes,
            };

            return this.covidCertificate;
        }

        try {
            const covidCertificate = await this.profileDao.verifyCovidCert({
                hash: stored.hash,
                secondaryHash: stored.secondaryHash,
            });

            this.covidCertificate = covidCertificate;

            if (covidCertificate.isValid) {
                return covidCertificate;
            }

            throw new Error("Certificate invalid.");
        } catch (ex) {
            logError(ex);
            this.covidCertificate = undefined;
            throw new Error("Could not verify certificate.");
        }
    }

    private localCovidCertCacheKey(): string {
        return "oc_" + this.identityService.getIdentityId()!.toLowerCase();
    }

    public isCovidValid(covidTrackingSettings: {
        verifyCovidCertMode?: CovidVerificationMode;
        showContactTracing?: boolean;
    }): Promise<CoronaSignupState> {
        //TODO remove true after everbody is migrated
        const covidVerificationIsActive =
            covidTrackingSettings.verifyCovidCertMode === CovidVerificationMode.TwoG ||
            covidTrackingSettings.verifyCovidCertMode === CovidVerificationMode.ThreeG ||
            covidTrackingSettings.verifyCovidCertMode === CovidVerificationMode.TwoGPlus;

        const profile = this.identityService.getProfile()!;

        return new Promise((resolve) => {
            if (!covidVerificationIsActive) {
                if (profile.firstName === undefined || profile.firstName === "") {
                    resolve(CoronaSignupState.MissingData);
                    return null;
                }

                if (covidTrackingSettings.showContactTracing === true) {
                    if (profile.phoneNumber === undefined || profile.phoneNumber === "") {
                        resolve(CoronaSignupState.MissingData);
                        return null;
                    }

                    if (profile.zip === undefined || profile.zip === "") {
                        resolve(CoronaSignupState.MissingData);
                        return null;
                    }
                }

                resolve(CoronaSignupState.Valid);
                return null;
            } else {
                return this.validateCovidCertificate(covidTrackingSettings.verifyCovidCertMode!)
                    .then(() => {
                        if (covidVerificationIsActive && profile.birthday === undefined) {
                            resolve(CoronaSignupState.CertificateVerification);
                            return null;
                        }

                        if (covidVerificationIsActive && profile.identityCard === undefined) {
                            resolve(CoronaSignupState.CertificateVerification);
                            return null;
                        }

                        if (profile.firstName === undefined || profile.firstName === "") {
                            resolve(CoronaSignupState.MissingData);
                            return null;
                        }

                        if (covidTrackingSettings.showContactTracing === true) {
                            if (profile.phoneNumber === undefined || profile.phoneNumber === "") {
                                resolve(CoronaSignupState.MissingData);
                                return null;
                            }

                            if (profile.zip === undefined || profile.zip === "") {
                                resolve(CoronaSignupState.MissingData);
                                return null;
                            }
                        }

                        resolve(CoronaSignupState.Valid);
                        return null;
                    })
                    .catch((ex) => {
                        logError(ex);
                        resolve(CoronaSignupState.CertificateVerification);
                        return null;
                    });
            }
        });
    }

    private static instance: UserCovidCertificateService;

    static getInstance(): UserCovidCertificateService {
        UserCovidCertificateService.instance =
            UserCovidCertificateService.instance || new UserCovidCertificateService();
        return UserCovidCertificateService.instance;
    }
}

export enum CoronaSignupState {
    MissingData,
    CertificateVerification,
    Valid,
}
